import type { ComponentType, SVGProps } from 'react';

import { cn } from '../cn';
import Tooltip from '../Input2/Tooltip';
import { Arrows, Charts, Education, Maps, Media, Time, Users } from '../StrokeIcons';

import { BucketTooltipBody } from './BucketTooltipBody';

// Per-bucket icons. Each signal type gets a distinctive icon so the panel reads
// as a row of meaningful tiles rather than identical placeholders.
// Buckets without an entry render no icon — text-only — instead of a placeholder.
type IconComponent = ComponentType<SVGProps<SVGSVGElement> & { className?: string }>;
const BUCKET_ICONS: Record<string, IconComponent> = {
  retired: Time.ClockCheck,
  employment_gap: Time.Hourglass01,
  job_change_rollover: Arrows.SwitchVertical02,
  rsu_cliff: Charts.LineChartDown01,
  long_tenure: Time.CalendarHeart02,
  age_proxy_63_64: Time.CalendarDate,
  age_proxy_65: Users.User03,
  senior_promotion: Education.Award01,
  location_modifier: Maps.Map01,
};

export type ContactSignalGridItem = {
  bucket_key: string;
  bucket_name: string;
  /** Number of sibling aspects in the same bucket (used to decide label density). */
  bucket_aspect_count: number;
  aspect_key: string;
  aspect_name: string;
  aspect_description: string | null;
  aspect_psychological_strategic_angle: string | null;
  value: number | null;
  reason: string | null;
};

export type ContactSignalsGridProps = {
  signals: ContactSignalGridItem[];
  isLoading?: boolean;
  lastUnipileFetchAt?: Date | string | null;
  /** Aspect keys ignored at the user+team level — triggered aspects in this set
   *  render with a dashed border to indicate "would have triggered, but ignored". */
  ignoredAspectKeys?: ReadonlySet<string>;
};

const SKELETON_TILE_COUNT = 9;

function formatRelativeTime(value: Date | string | null | undefined): string {
  if (value == null) return 'never';
  const d = value instanceof Date ? value : new Date(value);
  const ms = Date.now() - d.getTime();
  if (ms < 0) return 'just now';
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d ago`;
  const mo = Math.floor(day / 30);
  if (mo < 12) return `${mo}mo ago`;
  return `${Math.floor(mo / 12)}y ago`;
}

export function ContactSignalsGrid({
  signals,
  isLoading,
  lastUnipileFetchAt,
  ignoredAspectKeys,
}: ContactSignalsGridProps) {
  const unipileFetchTooltip =
    lastUnipileFetchAt != null
      ? `Last Unipile fetch: ${new Date(lastUnipileFetchAt).toLocaleString()}`
      : 'No Unipile data has ever been fetched for this contact';

  return (
    <div className="border-t border-gray-100 px-4 py-3 dark:border-gray-800">
      <div className="mb-2 flex items-center gap-2">
        <div className="flex flex-col">
          <div className="flex items-center gap-1.5">
            <Media.Signal01 className="size-3.5 text-gray-400" />
            <span className="text-sm font-semibold text-gray-900 dark:text-white">Signals</span>
          </div>
          {/* Only show last-sync line when there IS a sync — "never" was negative noise on a
              non-actionable line. Hover-tooltip still has the precise timestamp when present.
              "Unipile" is removed from the visible label since end-users don't care about provider names. */}
          {lastUnipileFetchAt != null && (
            <Tooltip as="span" tooltip={unipileFetchTooltip} className="text-[11px] text-gray-400">
              Last sync: {formatRelativeTime(lastUnipileFetchAt)}
            </Tooltip>
          )}
        </div>
      </div>
      {isLoading && (
        <div className="grid grid-cols-3 gap-2">
          {Array.from({ length: SKELETON_TILE_COUNT }).map((_, idx) => (
            <div
              key={idx}
              className="flex h-[52px] animate-pulse flex-col items-center justify-center gap-1 rounded-md border border-gray-200 bg-gray-100 dark:border-gray-800 dark:bg-gray-800/40"
            />
          ))}
        </div>
      )}
      {!isLoading && (() => {
        const anyComputed = signals.some((s) => s.value != null);
        if (signals.length === 0 || !anyComputed) {
          return (
            <p className="text-xs text-gray-400 dark:text-gray-500">
              Signals haven&apos;t been computed for this contact yet. Use Refresh on the
              contact header to fetch fresh data and re-evaluate.
            </p>
          );
        }
        const triggeredBuckets = groupSignalsByBucket(signals).filter((bucket) =>
          bucket.aspects.some((a) => (a.value ?? 0) > 0),
        );
        if (triggeredBuckets.length === 0) {
          return (
            <p className="text-xs text-gray-400 dark:text-gray-500">No signals triggered.</p>
          );
        }
        return (
          <div className="grid grid-cols-3 gap-2">
            {triggeredBuckets.map((bucket) => {
            const triggeredAspects = bucket.aspects.filter((a) => (a.value ?? 0) > 0);
            const triggered = triggeredAspects.length > 0;
            const hasValue = bucket.aspects.some((a) => a.value != null);
            const bucketExcluded =
              ignoredAspectKeys != null &&
              bucket.aspects.length > 0 &&
              bucket.aspects.every((a) => ignoredAspectKeys.has(a.aspect_key));
            // Green only when there is an active trigger — i.e. at least one triggered
            // aspect that is NOT in the ignored set. If every triggered aspect is ignored,
            // the bucket reads as "would have fired but doesn't count" (muted/dashed).
            const hasActiveTrigger =
              triggered &&
              triggeredAspects.some((a) => !(ignoredAspectKeys?.has(a.aspect_key) ?? false));
            const triggeredAllIgnored = triggered && !hasActiveTrigger;
            const Icon = BUCKET_ICONS[bucket.id];
            return (
              <Tooltip
                key={bucket.id}
                as="div"
                tooltip={<BucketTooltipBody bucket={bucket} ignoredAspectKeys={ignoredAspectKeys} />}
                tooltipMaxWidth={320}
                tooltipClassName="max-h-[80vh] overflow-y-auto"
                color={hasActiveTrigger ? 'green' : 'gray'}
                className={cn(
                  // Triggered: a left accent bar (via border-l-4) plus solid green fill makes the
                  // tile pop hard against muted neighbors. Untriggered tiles keep a normal 1px border.
                  'flex h-full cursor-pointer items-center gap-2 rounded-md px-2 py-2 text-xs transition-colors',
                  hasActiveTrigger &&
                    'border border-l-4 border-green-500/60 border-l-green-500 bg-green-50 text-green-800 hover:bg-green-100 dark:bg-green-900/30 dark:text-green-300 dark:hover:bg-green-900/40',
                  // Triggered but every triggered aspect is ignored — render neutral with a
                  // dashed border so it reads as "would have fired but is disabled", not green.
                  triggeredAllIgnored &&
                    'border border-dashed border-gray-300 bg-gray-50 text-gray-500 hover:bg-gray-100 dark:border-gray-600 dark:bg-gray-800/40 dark:text-gray-400 dark:hover:bg-gray-800/60',
                  !triggered &&
                    hasValue &&
                    'border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800/30 dark:text-gray-400 dark:hover:bg-gray-800/50',
                  // No data: solid faint-gray fill. Lower visual frequency than dashed
                  // borders — the panel reads as a calm grid instead of nine "broken" tiles.
                  !hasValue &&
                    'border border-gray-200 bg-gray-50 text-slate-500 hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-800/40 dark:text-slate-400 dark:hover:bg-gray-800/60',
                  // Excluded: diagonal stripes overlay so the tile reads as disabled at
                  // a glance, regardless of triggered/value state.
                  bucketExcluded &&
                    'bg-[repeating-linear-gradient(45deg,transparent_0,transparent_5px,rgba(15,23,42,0.06)_5px,rgba(15,23,42,0.06)_6px)] opacity-60 dark:bg-[repeating-linear-gradient(45deg,transparent_0,transparent_5px,rgba(255,255,255,0.06)_5px,rgba(255,255,255,0.06)_6px)]',
                )}>
                {Icon != null && (
                  <Icon
                    className={cn(
                      'shrink-0',
                      hasActiveTrigger && 'size-4 text-green-600 dark:text-green-400',
                      triggeredAllIgnored && 'size-4 text-gray-400 dark:text-gray-500',
                      !triggered && 'size-3.5 opacity-50',
                    )}
                  />
                )}
                <span
                  className={cn(
                    'truncate leading-tight',
                    hasActiveTrigger && 'font-bold',
                    triggeredAllIgnored && !bucketExcluded && 'font-bold',
                    bucketExcluded && 'line-through decoration-gray-400',
                    !triggered && 'font-semibold',
                  )}>
                  {bucket.name}
                </span>
              </Tooltip>
            );
          })}
          </div>
        );
      })()}
    </div>
  );
}

type GroupedBucket = {
  id: string;
  name: string;
  aspects: ContactSignalGridItem[];
};

function groupSignalsByBucket(signals: ContactSignalGridItem[]): GroupedBucket[] {
  const map = new Map<string, GroupedBucket>();
  for (const sig of signals) {
    const existing = map.get(sig.bucket_key);
    if (existing) {
      existing.aspects.push(sig);
    } else {
      map.set(sig.bucket_key, {
        id: sig.bucket_key,
        name: sig.bucket_name,
        aspects: [sig],
      });
    }
  }
  return Array.from(map.values());
}
